import { extend, first, get, isEqual, pick, result, size } from 'underscore';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ErrorMessages } from '@bingads-webui-react/validation';
import { ActuationGroup } from '@bingads-webui-campaign-react/actuator';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { FormErrorGroup, UnmappedFormErrors } from '@bingads-webui-campaign-react/form-error';
import { useFormService, FORM_STATES } from '@bingads-webui-react/form';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { withServerContext } from '@bingads-webui-campaign-react/server-context';
import { withScope, QueryParam } from '@bingads-webui-react/scope-service';
import { viewModelToModelInResponseForAd } from '@bingads-webui-campaign-react/search-campaign-data';

import { getStyles } from './styles';
import { AdTypeSelector } from './ad-type-selector';
import { AdFooter } from './ad-footer';
import { Editors } from './editors/index';
import { StepHeader } from '../../shared/step-header';

const errorFilter = error => error.message;
const errorMapper = error => error.message;

const AdEditorInternal = (props) => {
  const {
    data,
    onDataChange,
    error,
    permissions,
    serverContext,
    scope,
    wizardPerfMarker,
    adTypes,
    enableAdsManagment,
    confirmWhenSwitchAdType,
  } = props;
  const {
    serverErrors = [],
    systemErrors = [],
  } = error || {};

  const editorRef = React.createRef();
  const classes = useDefaultStyles(getStyles);
  const i18n = useContext(I18nContext);
  const perfMarker = useMemo(() => wizardPerfMarker.createChild('AddTextStep', { autoEnd: true }), [wizardPerfMarker]);

  const {
    state: {
      context: {
        saveFailed: showAllValidationErrors,
      },
      value: formStateValue,
    },
  } = useFormService();

  const {
    campaignId,
    adGroupId,
    ads,
    adType: initialAdType,
    enableAdTypeSelector,
    isEmpty,
    loaded,
  } = data;

  const entityScope = useMemo(() => {
    const { values: { aid, cid, uid } } = scope;
    return {
      accountId: aid,
      advertiserCustomerId: cid,
      customerId: cid,
      userId: uid,
      campaignType: 1, // Search
      campaignId,
      adGroupId,
    };
  }, [adGroupId, campaignId, scope]);

  const [adType, setAdType] = useState(initialAdType || first(adTypes));
  const [initialAd, setInitialAd] = useState(get(ads, adType, {}));
  const apiErrors = useMemo(() => get(ads[adType], 'serverErrors'), [adType, ads]);

  const onAdTypeChanged = useCallback((newAdType) => {
    setAdType(newAdType);
    setInitialAd(viewModelToModelInResponseForAd(ads[newAdType], newAdType));
  }, [ads]);

  const onAdChanged = useCallback((ad) => {
    extend(ad.data, pick(get(initialAd, 'data', {}), ['Id']));
    const newData = {
      data: {
        ...data,
        ads: {
          ...data.ads,
          [adType]: ad,
        },
        adType,
      },
      isValid: ad.isValid,
    };
    if (!isEqual(data, newData.data)) {
      onDataChange(newData);
    }
  }, [adType, data, initialAd, onDataChange]);

  const onAdExtensionsChanged = useCallback((adExtensions) => {
    const newData = {
      data: {
        ...data,
        adExtensions,
      },
    };
    if (!isEqual(data, newData.data)) {
      onDataChange(newData);
    }
  }, [data, onDataChange]);

  const renderEditor = useMemo(() => {
    const Editor = Editors[adType];
    if (!Editor) {
      return null;
    }
    return (
      <Editor
        ad={initialAd}
        i18n={i18n}
        onAdChanged={onAdChanged}
        onAdExtensionsChanged={onAdExtensionsChanged}
        perfMarker={perfMarker}
        permissions={permissions}
        scope={entityScope}
        serverContext={serverContext}
        showAllValidationErrors={showAllValidationErrors}
        apiErrors={apiErrors}
        editorRef={editorRef}
      />
    );
  }, [adType, initialAd, i18n, onAdChanged, onAdExtensionsChanged, perfMarker, permissions, entityScope, serverContext, showAllValidationErrors, apiErrors, editorRef]);

  useEffect(() => {
    if (editorRef && editorRef.current && formStateValue === FORM_STATES.SAVING_SUCCEEDED) {
      editorRef.current.logAppliedSuggestions(result(ads[adType], 'data', {}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formStateValue]);

  useEffect(() => {
    if (loaded) {
      const loadedAdType = data.adType || first(adTypes);
      setAdType(loadedAdType);
      setInitialAd(ads[loadedAdType]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  return loaded && (
    <div className={classNames(classes.addTextStep, 'add-text-step-editor')}>
      <ActuationGroup actuationProps={{ isActive: false }}>
        <AttentionBeaconGroup>
          <FormErrorGroup formErrorProps={{ errors: serverErrors, showAllValidationErrors }}>
            <StepHeader
              title={_TL_('Create your ad')}
              description={_TL_('Add the images, text, and URLs to use for your ad.')}
            />
            <div className="server-errors">
              <UnmappedFormErrors filter={errorFilter} mapper={errorMapper} />
            </div>
            <div className="system-errors">
              <ErrorMessages messages={systemErrors} />
            </div>
            {size(adTypes) > 1 && <AdTypeSelector
              i18n={i18n}
              adTypes={adTypes}
              selectedAdType={adType}
              onAdTypeChanged={onAdTypeChanged}
              enableAdTypeSelector={enableAdTypeSelector}
              confirmWhenSwitchAdType={confirmWhenSwitchAdType}
            />}
            {renderEditor}
            {enableAdsManagment && (
              <AdFooter
                i18n={i18n}
                enableCreateAnother={enableAdsManagment}
                enableCancel={!isEmpty}
              />
            )}
          </FormErrorGroup>
        </AttentionBeaconGroup>
      </ActuationGroup>
    </div>
  );
};

AdEditorInternal.propTypes = {
  data: PropTypes.shape({
    campaignId: PropTypes.number,
    adGroupId: PropTypes.number,
    ads: PropTypes.shape({}),
    adType: PropTypes.string,
    enableAdTypeSelector: PropTypes.bool,
    isEmpty: PropTypes.bool,
    loaded: PropTypes.bool,
  }),
  onDataChange: PropTypes.func.isRequired,
  error: PropTypes.shape({
    serverErrors: PropTypes.arrayOf(PropTypes.shape({})),
    systemErrors: PropTypes.arrayOf(PropTypes.string),
  }),
  permissions: PropTypes.shape({
    IsImpressionTrackingUrlsEnabled: PropTypes.bool,
  }).isRequired,
  serverContext: PropTypes.shape({}).isRequired,
  scope: PropTypes.instanceOf(QueryParam).isRequired,
  wizardPerfMarker: PropTypes.shape({
    createChild: PropTypes.func.isRequired,
  }).isRequired,
  adTypes: PropTypes.arrayOf(PropTypes.string),
  enableAdsManagment: PropTypes.bool,
  confirmWhenSwitchAdType: PropTypes.bool,
};

AdEditorInternal.defaultProps = {
  data: {
    campaignId: -1,
    adGroupId: -1,
    ads: {},
    enableAdTypeSelector: true,
    loaded: false,
  },
  error: null,
  adTypes: [],
  enableAdsManagment: false,
  confirmWhenSwitchAdType: false,
};

export const AdEditor = withPermissions(withServerContext(withScope(AdEditorInternal)));
